import { useState } from "react";
import { Users, X, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const field = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";
const EMPTY = { full_name: "", phone: "", email: "", fargo_rating: "", divisions: "", agreed: false, company: "" };
const DIVISIONS = ["Sunday Trios", "Tuesday 8-Ball", "Both"];

export function SpareSignup({ autoOpen = false }) {
  const [open, setOpen] = useState(autoOpen);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openModal = () => { setForm(EMPTY); setResult(null); setOpen(true); };
  const close = () => { if (!busy) setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.error("Please enter your full name and phone number.");
      return;
    }
    if (!form.email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!form.divisions) {
      toast.error("Please choose which division(s) you'd like to spare for.");
      return;
    }
    if (!form.agreed) {
      toast.error("Please confirm the sharing agreement to join the spare list.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/spares", form);
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid="join-spare-list-btn"
        className="inline-flex items-center gap-2 bg-white text-[#0B0B0C] hover:bg-gray-100 transition-colors rounded-md px-6 py-3 font-semibold"
      >
        <Users className="h-4 w-4" /> Join Spare List
      </button>

      {open && (
        <div className="fixed inset-0 z-[75] bg-black/60 flex items-center justify-center p-4" onClick={close} data-testid="spare-modal">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
              <h3 className="font-heading font-bold text-lg inline-flex items-center gap-2">
                <Users className="h-5 w-5 text-[#E11D2A]" /> Join the Spare List
              </h3>
              <button onClick={close} data-testid="spare-close" className="text-[#999999] hover:text-[#111111]"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5">
              {result ? (
                <div className="text-center py-6 space-y-4" data-testid="spare-success">
                  <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
                  <p className="text-[#333333] leading-relaxed" data-testid="spare-success-message">{result.message}</p>
                  <button onClick={() => setOpen(false)} data-testid="spare-done-btn" className="inline-flex items-center justify-center bg-[#1E63C4] text-white rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-[#174e9c]">Close</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3.5">
                  <div className="bg-[#62A8E5]/10 border border-[#62A8E5]/30 rounded-lg p-3.5 text-sm text-[#333333] leading-relaxed">
                    I would like to be added to the spare list and understand that all players must be sanctioned with the BCA before playing. The $30 annual sanctioning fee must be paid the first night prior to play. <span className="font-semibold">Please note: this spare-list sign-up is for the current session only.</span>
                  </div>
                  <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" aria-hidden="true" />
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">Full Name <span className="text-[#E11D2A]">*</span></label>
                    <input className={field} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="spare-name" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">Phone Number <span className="text-[#E11D2A]">*</span></label>
                    <input className={field} type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="spare-phone" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">Email <span className="text-[#E11D2A]">*</span></label>
                    <input className={field} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="spare-email" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">Fargo Rating <span className="text-[#888888] text-xs">(optional)</span></label>
                    <input className={field} value={form.fargo_rating} onChange={(e) => set("fargo_rating", e.target.value)} data-testid="spare-fargo" placeholder="e.g. 525" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1.5">Divisions to spare <span className="text-[#E11D2A]">*</span></label>
                    <div className="grid grid-cols-3 gap-2">
                      {DIVISIONS.map((d) => (
                        <button
                          type="button"
                          key={d}
                          onClick={() => set("divisions", d)}
                          data-testid={`spare-division-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`rounded-md border px-2 py-2 text-xs font-semibold transition-colors ${
                            form.divisions === d ? "border-[#1E63C4] bg-[#1E63C4] text-white" : "border-[#E5E7EB] text-[#333333] hover:border-[#1E63C4]"
                          }`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer text-sm text-[#333333] leading-relaxed">
                    <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#1E63C4] shrink-0" checked={form.agreed} onChange={(e) => set("agreed", e.target.checked)} data-testid="spare-agree" />
                    <span>I&apos;m agreeing to allow my name, telephone number and fargo rating to be shared with team captains of &lsquo;Quinte BCA Pool League&rsquo; when requested for the purposes of sparing in the league.</span>
                  </label>
                  <button type="submit" disabled={busy} data-testid="spare-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#E11D2A] text-white rounded-md px-4 py-2.5 font-semibold hover:bg-[#c41320] disabled:opacity-60">
                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : "Submit"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
